import React, { useEffect, useState } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { useClickOutside } from "../../Hooks/useClickOutside";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import axios from "axios";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

const AddSubCategoryModal = ({ onClose, isSubAddModal, onSuccess, subCategoryData }) => {
  const [formData, setFormData] = useState({
    name: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
    description: {
      en: "",
      hi: "",
      es: "",
      zh: ""
    },
    categoryId: subCategoryData?.category?._id || "",
    imgUrl: subCategoryData?.imgUrl || "",
    catIconUrl: subCategoryData?.catIconUrl || "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [animate, setAnimate] = useState(false);
  const domNode = useClickOutside(() => handleClose());

  // Helper to get multilingual data from API response
  const getMultilingualValue = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (typeof value === "object") {
      return {
        en: value.en || value.english || "",
        hi: value.hi || value.hindi || "",
        es: value.es || value.spanish || "",
        zh: value.zh || value.chinese || "",
      };
    }
    return "";
  };

  // Helper to get English name for display
  const getEnglishName = (category) => {
    if (!category || !category.name) return "";
    if (typeof category.name === 'string') return category.name;
    if (typeof category.name === 'object') {
      return category.name.en || category.name.english || 
             Object.values(category.name)[0] || 
             "Unnamed Category";
    }
    return "Category";
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/categories/get-all-categories`);
        setCategories(res.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // Set form data when editing
  useEffect(() => {
    if (subCategoryData) {
      setFormData({
        name: getMultilingualValue(subCategoryData.name),
        description: getMultilingualValue(subCategoryData.description),
        categoryId: subCategoryData.category?._id || "",
        imgUrl: subCategoryData.imgUrl || "",
        catIconUrl: subCategoryData.catIconUrl || "",
      });
    }
  }, [subCategoryData]);

  useEffect(() => {
    if (isSubAddModal) setTimeout(() => setAnimate(true), 10);
  }, [isSubAddModal]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleChange = (e, field, lang = null) => {
    const { value } = e.target;
    
    if (field === "name" || field === "description") {
      setFormData(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.en?.trim()) {
      toast.error("English name is required");
      return;
    }
    
    if (!formData.description.en?.trim()) {
      toast.error("English description is required");
      return;
    }
    
    if (!formData.categoryId) {
      toast.error("Please select a category");
      return;
    }

    try {
      setLoading(true);

      let imgUrl = formData.imgUrl;
      let catIconUrl = formData.catIconUrl;

      if (imageFile) {
        const uploadedImg = await uploadToCloudinary(imageFile);
        imgUrl = uploadedImg.secure_url; 
      }

      if (iconFile) {
        const uploadedIcon = await uploadToCloudinary(iconFile);
        catIconUrl = uploadedIcon.secure_url; 
      }

      const finalData = {
        name: {
          en: formData.name.en?.trim() || "",
          hi: formData.name.hi?.trim() || formData.name.en?.trim() || "",
          es: formData.name.es?.trim() || formData.name.en?.trim() || "",
          zh: formData.name.zh?.trim() || formData.name.en?.trim() || "",
        },
        description: {
          en: formData.description.en?.trim() || "",
          hi: formData.description.hi?.trim() || formData.description.en?.trim() || "",
          es: formData.description.es?.trim() || formData.description.en?.trim() || "",
          zh: formData.description.zh?.trim() || formData.description.en?.trim() || "",
        },
        categoryId: formData.categoryId,
        imgUrl,
        catIconUrl,
      };

      const url = subCategoryData
        ? `${process.env.REACT_APP_API_URL}/sub-categories/update-sub-category/${subCategoryData._id}`
        : `${process.env.REACT_APP_API_URL}/sub-categories/create-sub-category`;

      const method = subCategoryData ? axios.put : axios.post;

      const response = await method(url, finalData);

      if (response.status >= 200 && response.status < 205) {
        Swal.fire("Success", response.data.message, "success");
        onSuccess();
        handleClose();
      }
    } catch (error) {
      console.error("Subcategory save error:", error);
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className={`fixed inset-0 z-50 flex justify-center items-center bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
        animate ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        ref={domNode}
        className={`bg-[#ecf1f5] relative w-[95%] sm:w-[90%] md:w-[85%] lg:w-[80%] xl:w-[75%] 
        max-h-[90vh] overflow-y-auto p-4 sm:p-6 rounded-xl transform transition-all duration-300
        ${animate ? "scale-100 opacity-100 translate-y-0" : "scale-95 opacity-0 translate-y-5"}`}
      >
        {/* Close Button */}
        <div className="absolute top-3 right-3 z-10">
          <button
            onClick={handleClose}
            className="p-2 rounded-full bg-white hover:text-[#2275fc] transition-colors duration-300"
          >
            {icons.closeIcon}
          </button>
        </div>

        <h1 className="text-xl sm:text-2xl text-primaryTextColor font-philosopher mb-4 capitalize text-center sm:text-left">
          {subCategoryData ? "Edit Subcategory" : "Add Subcategory"} (Multilingual)
        </h1>

        <form onSubmit={handleSubmit} className="flex flex-col space-y-4 sm:space-y-6">
          {/* Category Dropdown */}
          <div className="w-full md:w-1/2">
            <div className="flex flex-col space-y-2">
              <label className="text-sm sm:text-base font-medium text-gray-700">
                Select Parent Category <span className="text-[#ff5200]">*</span>
              </label>
              <select
                name="categoryId"
                required
                value={formData.categoryId}
                onChange={(e) => handleChange(e, "categoryId")}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm sm:text-base outline-none focus:ring-2 ring-[#2275fc]"
              >
                <option value="">-- Select a category --</option>
                {categories.map((cat) => (
                  <option key={cat._id} value={cat._id}>
                    {getEnglishName(cat)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Subcategory Names - All Languages */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-medium text-gray-700">
              Subcategory Name <span className="text-[#ff5200]">*</span>
              <span className="text-xs text-gray-500 ml-2">(Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* English Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English *</span>
                  {!formData.name.en?.trim() && (
                    <span className="text-xs text-red-500">Required</span>
                  )}
                </div>
                <input
                  required
                  onChange={(e) => handleChange(e, "name", "en")}
                  value={formData.name.en || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-blue-300 bg-blue-50 w-full"
                  type="text"
                  placeholder="English name"
                />
              </div>

              {/* Hindi Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "hi")}
                  value={formData.name.hi || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Hindi name"
                />
              </div>

              {/* Spanish Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "es")}
                  value={formData.name.es || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Spanish name"
                />
              </div>

              {/* Chinese Name */}
              <div className="relative">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <input
                  onChange={(e) => handleChange(e, "name", "zh")}
                  value={formData.name.zh || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full"
                  type="text"
                  placeholder="Chinese name"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English name is required. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Subcategory Descriptions - All Languages */}
          <div className="space-y-3">
            <label className="text-sm sm:text-base font-medium text-gray-700">
              Description <span className="text-[#ff5200]">*</span>
              <span className="text-xs text-gray-500 ml-2">(Fill in all languages)</span>
            </label>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* English Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇺🇸 English *</span>
                  {!formData.description.en?.trim() && (
                    <span className="text-xs text-red-500">Required</span>
                  )}
                </div>
                <textarea
                  required
                  onChange={(e) => handleChange(e, "description", "en")}
                  value={formData.description.en || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-blue-300 bg-blue-50 w-full h-28"
                  placeholder="English description"
                />
              </div>

              {/* Hindi Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇮🇳 Hindi</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "hi")}
                  value={formData.description.hi || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-28"
                  placeholder="Hindi description"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Spanish Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇪🇸 Spanish</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "es")}
                  value={formData.description.es || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-28"
                  placeholder="Spanish description"
                />
              </div>

              {/* Chinese Description */}
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">🇨🇳 Chinese</span>
                </div>
                <textarea
                  onChange={(e) => handleChange(e, "description", "zh")}
                  value={formData.description.zh || ""}
                  className="outline-none focus:ring-2 ring-[#2275fc] px-3 py-2.5 rounded-lg border border-gray-300 w-full h-28"
                  placeholder="Chinese description"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500">
              English description is required. Other languages will fallback to English if left empty.
            </p>
          </div>

          {/* Uploads */}
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {/* Image Upload */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm sm:text-base font-medium text-gray-700">Subcategory Image</label>
              <label
                htmlFor="imgUrl"
                className="border-2 border-dashed border-blue-400 overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center bg-blue-50/50 hover:bg-blue-50 transition-colors"
              >
                <span className="text-blue-500 text-3xl sm:text-4xl z-10">{icons.uploadIcon}</span>
                <p className="z-10 text-xs sm:text-sm mt-2">
                  Drop your image here or <span className="text-blue-600 font-medium">click to browse</span>
                </p>
                <p className="z-10 text-xs text-gray-500 mt-1">
                  Recommended: 800x600px or larger
                </p>
                {(imageFile || formData.imgUrl) && (
                  <img
                    src={imageFile ? URL.createObjectURL(imageFile) : formData.imgUrl}
                    alt="Preview"
                    className="h-full w-full object-cover absolute top-0 opacity-30"
                  />
                )}
              </label>
              <input
                onChange={(e) => setImageFile(e.target.files[0])}
                type="file"
                accept="image/*"
                id="imgUrl"
                className="hidden"
              />
            </div>

            {/* Icon Upload */}
            <div className="flex flex-col space-y-2">
              <label className="text-sm sm:text-base font-medium text-gray-700">Subcategory Icon</label>
              <label
                htmlFor="catIconUrl"
                className="border-2 border-dashed border-green-400 overflow-hidden relative flex-1 py-10 px-3 sm:px-5 rounded-xl flex items-center justify-center border-2 flex-col cursor-pointer border-dashed text-center bg-green-50/50 hover:bg-green-50 transition-colors"
              >
                <span className="text-green-500 text-3xl sm:text-4xl z-10">{icons.uploadIcon}</span>
                <p className="z-10 text-xs sm:text-sm mt-2">
                  Drop your icon here or <span className="text-green-600 font-medium">click to browse</span>
                </p>
                <p className="z-10 text-xs text-gray-500 mt-1">
                  Recommended: 128x128px SVG or PNG
                </p>
                {(iconFile || formData.catIconUrl) && (
                  <img
                    src={iconFile ? URL.createObjectURL(iconFile) : formData.catIconUrl}
                    alt="Preview"
                    className="h-full w-full object-cover absolute top-0 opacity-30"
                  />
                )}
              </label>
              <input
                onChange={(e) => setIconFile(e.target.files[0])}
                type="file"
                accept="image/*"
                id="catIconUrl"
                className="hidden"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="flex justify-end items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2.5 mr-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors duration-300 font-medium"
            >
              Cancel
            </button>
            <button
              disabled={loading}
              type="submit"
              className="text-white flex items-center justify-center bg-[#b07d17] rounded-lg hover:bg-[#8c6312] transition-colors duration-300 px-8 font-bold py-2.5 text-sm sm:text-base min-w-[120px]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {subCategoryData ? "Updating..." : "Creating..."}
                </>
              ) : subCategoryData ? "Update Subcategory" : "Create Subcategory"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default AddSubCategoryModal;